<template>
  <div class="w-full flex px-4 pb-7" :style="{ height: `${barAreaHeight + 44}px` }">
    <!-- Axis Labels -->
    <div class="h-full flex flex-col-reverse justify-between pr-3 flex-shrink-0">
      <BaseText v-for="label in axisLabels" :key="`mono-label-${label}`"
      type="body" size="xs" class="text-text-subdued">
        {{ label }}
      </BaseText>
    </div>
    <!-- Graph Area -->
    <div class="relative h-full flex-grow flex items-end justify-between py-2"
    :style="{ paddingLeft: `${graphXPadding}px`, paddingRight: `${graphXPadding}px` }">
      <!-- Plot Lines -->
      <div class="absolute left-0 top-2 bottom-2 right-0 flex flex-col justify-between">
        <div v-for="n in numPlotLines" :key="`mono-plot-line-${n}`" class="w-full h-0 border-t border-border-normal"/>
      </div>
      <!-- Groups / Bars -->
      <div v-for="(group, index) in barDataGroups" :key="`mono-group-${index}`" class="relative flex items-end">
        <div v-for="(bar, barIndex) in group.bars" :key="`mono-bar-${index}-${barIndex}-${bar.label}`" 
        class="relative flex justify-center" :style="{ width: `${barSpacing}px` }">
          <!-- Bar -->
          <div class="w-2 graph-bar" style="border-radius: 3px; min-height: 2px;"
          :style="{'--bar-height': `${bar.height}px`, backgroundColor: barColors[barIndex]}" 
          @animationstart="initiateAnimLabelOffset"/>
          <!-- Bar Label -->
          <div :ref="`bar-label-${index}`" 
          class="bar-label absolute -top-5 left-1/2 transform -translate-x-1/2 px-0.5">
            <BaseText type="label" size="xs" class="text-text-muted whitespace-nowrap">
              {{ bar.label }}
            </BaseText>
          </div>
        </div>
        <!-- Group Label -->
        <div class="absolute -bottom-7 left-1/2 transform -translate-x-1/2">
          <BaseText type="body" size="xs" class="text-text-subdued whitespace-nowrap">
            {{ group.label.text }}
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { scaleLinear } from 'd3-scale'
import formatLabelString from '@/utils/lens/formatGraphLabelString'

const MAX_AXIS_LABELS = 6

export default {
  name: 'MonoMetricBreakdownBarGraph',
  props: {
    selectedMetric: {
      type: String,
      required: true
    },
    dataGroups: {
      type: Array,
      required: true
    },
    currencyRegion: {
      type: String,
      default: 'USD'
    },
    barColors: {
      type: Array,
      default: () => ['#0058CE', '#443190']
    },
    barAreaHeight: {
      type: Number,
      default: 112
    },
    graphXPadding: {
      type: Number,
      default: 12
    },
    barSpacing: {
      type: Number,
      default: 32
    }
  },
  data () {
    return {
      axisScale: null,
      axisLabels: [],
      
      // UI
      numPlotLines: 0,
      animatingLabelOffset: false,
    }
  },
  watch: {
    dataGroups: {
      handler () {
        this.initializeAxisInformation()
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    barDataGroups () {
      if (!this.axisScale || !this.axisLabels.length) return []
      return this.dataGroups.map(group => ({
        ...group,
        bars: group.values.map(value => ({
          height: this.axisScale(value),
          label: this.formatAxisLabelString(value),
        }))
      }))
    }
  },
  methods: {
    initializeAxisInformation () {
      if (!this.dataGroups.length || !this.selectedMetric) {
        this.axisScale = null
        return
      }
      this.computeAxisScales()
    },
    computeAxisScales () {
      if (!this.dataGroups?.[0]?.values?.length) return

      // Configure the axis scale
      const values = this.dataGroups.flatMap(group => group.values)
      let maxValue = Math.max(...values)
      if (maxValue === 0) maxValue = 1
      this.axisScale = scaleLinear()  
        .domain([0, maxValue])
        .range([0, this.barAreaHeight])
        .nice()

      // Compute the axis labels
      let ticks = this.axisScale.ticks(MAX_AXIS_LABELS)
      const reduceTicks = (ticks) => {
        // Ensures that the number of ticks is less than or equal to MAX_AXIS_LABELS
        let tickValues = [...ticks]
        if (ticks.length % 2 === 0) {
          const newMax = ticks[ticks.length - 1] + ticks[1]
          tickValues.push(newMax)
          this.axisScale.domain([0, newMax])
        }
        while (tickValues.length > MAX_AXIS_LABELS) {
          tickValues = tickValues.filter((_, index) => index % 2 === 0)
        }
        return tickValues
      }
      ticks = reduceTicks(ticks)
      this.axisLabels = ticks.map(tick => this.formatAxisLabelString(tick))
      this.numPlotLines = ticks.length
    },
    formatAxisLabelString (value) {
      const type = this.getMetricLookup?.[this.selectedMetric]?.type
      return formatLabelString(value, type, this.currencyRegion)
    },
    initiateAnimLabelOffset () {
      if (this.animatingLabelOffset) return
      this.animatingLabelOffset = true

      // If we are animating, continuously compute the label offset until the animation is complete
      const startTime = performance.now()
      const animateLabelOffset = (currentTime) => {
        if (currentTime - startTime < 750) { // 750ms is the duration of the animation
          this.handleBarLabelCollision()
          requestAnimationFrame(animateLabelOffset)
        } else {
          this.animatingLabelOffset = false
        }
      }
      requestAnimationFrame(animateLabelOffset)
    },
    handleBarLabelCollision () {
      const getIsCollision = (rectA, rectB) => {
        // Returns true if the bounding boxes overlap
        return (
          rectA.left < rectB.left + rectB.width &&
          rectA.left + rectA.width > rectB.left &&
          rectA.top < rectB.top + rectB.height &&
          rectA.top + rectA.height > rectB.top
        )
      }

      for (let i = 0; i < this.barDataGroups.length; i++) {
        const barLabels = this.$refs[`bar-label-${i}`]
        if (!barLabels || barLabels.length <= 1) continue
        barLabels.forEach(label => { label.style.top = '-20px' })

        barLabels.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)

        for (let j = 0; j < barLabels.length - 1; j++) {
          const labelA = barLabels[j]
          const labelB = barLabels[j + 1]
          const labelARect = labelA.getBoundingClientRect()
          const labelBRect = labelB.getBoundingClientRect()
          if (getIsCollision(labelARect, labelBRect)) {
            const shiftOffset = (labelARect.top + labelARect.height) - labelBRect.top - 20
            labelB.style.top = `${shiftOffset}px`
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.graph-bar {
  height: var(--bar-height);
  animation: barEnter 0.75s ease-out forwards;
}
@keyframes barEnter {
  from { height: 0px; }
  to { height: var(--bar-height); }
}
.bar-label {
  z-index: 5;
  text-shadow:
    -1px -1px 0 #fff,
     1px -1px 0 #fff,
    -1px  1px 0 #fff,
     1px  1px 0 #fff;
}
</style>